import { useEffect } from "react";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Marker, Polyline, Popup } from "react-leaflet";
import { toast } from "react-toastify";
import { DivIcon } from "leaflet";

// Actions
import {
  clearWaypointsRoute,
  getWaypointsRoute,
  resetRoadStretches,
} from "../../../actions/org/OrgRoadStretchActions";

// Constants and Utils
import {
  getCoordinates,
  isValidCoordinate,
} from "../../../common-utils/geo-util/coordinatesUtil";
import { India } from "../../../constants/GeneralConstants";
import { SelectedRouteStyle } from "../../../constants/CssConstants";
import { getMapZoomLevel } from "../../mavin-tools/ToolsUtils";

// Components import
import LLMap from "../../../components/map/leaflet-map/LLMap";
import MapIcon from "../../../components/map/map-icon/MapIcon";
import PinDropLocationAndZoom from "../../../components/map/pin-drop-select/PinDropLocationAndZoom";
import PlacesAutocomplete from "../../../components/places-autocomplete/PlacesAutocomplete";
import { ButtonWithLoader } from "../../../mavin/components/button/Button";

// Page Functions
function getViaLocationIcon(index) {
  const htmlClass = "rounded-circle p-2 bg-warning font-weight-bold";
  const locationTag = index + 1;

  return new DivIcon({
    className: ``,
    html: `<span class="${htmlClass}">${locationTag}</span>`,
  });
}

// Page Components
// ----------------------------------------------------------------------------
function ViaMidPointsSection({
  viaLocationInputs = [],
  handleViaLocationSearch = () => {},
  handleRemoveLocationInput = () => {},
}) {
  if (viaLocationInputs.length === 0) {
    return null;
  }

  return (
    <>
      {viaLocationInputs.map((input, index) => {
        const { locationStr, id } = input;
        return (
          <div key={id} className="d-flex align-items-center mb-2">
            <span className="rounded-circle bg-warning font-weight-bold p-1 px-2 mr-2">
              {index + 1}
            </span>
            <PlacesAutocomplete
              className="shadow-none rounded-lg"
              placeholder={"Via"}
              locationStr={locationStr}
              setLocationStr={(locationStr) =>
                handleViaLocationSearch(locationStr, index)
              }
            />
            <i
              className="fa fa-minus fs-6 p-1 bg-alt ml-2 rounded-circle border cursor-pointer"
              onClick={() => handleRemoveLocationInput(index)}
            />
          </div>
        );
      })}
    </>
  );
}

function AddViaPointsAndGetRoutesSection({
  handleAddLocationInput = () => {},
  handleGetRoutes = () => {},
}) {
  // loading
  const waypointsRouteLoading = useSelector(
    (state) => state.orgRoadStretch.waypointsRouteLoading
  );

  return (
    <div className="d-flex align-items-center justify-content-between">
      <div
        className="d-flex align-items-center cursor-pointer p-2 text-primary"
        onClick={handleAddLocationInput}
      >
        <i className="fa fa-plus-circle fs-6" />
        <span className="font-italic font-weight-bold pl-1">
          Add Via Location
        </span>
      </div>

      <ButtonWithLoader
        className="btn btn-primary px-3 shadow-none shrink-0 py-1"
        displayContent="Get Routes"
        loader={waypointsRouteLoading}
        isDisabled={waypointsRouteLoading}
        onClickFunction={handleGetRoutes}
      />
    </div>
  );
}

function LocationSearch({
  startLocationStr,
  setStartLocationStr,
  endLocationStr,
  setEndLocationStr,
  handleGetRoutes = () => {},
  viaLocationInputs,
  setViaLocationInputs = () => {},
}) {
  // show hide search box
  const [showLocationSearch, setShowLocationSearch] = useState(true);

  // add location
  function handleAddLocationInput() {
    setViaLocationInputs((prevInputs) => [
      ...prevInputs,
      {
        id: `${Date.now()}_${Math.random()}`,
        locationStr: "",
        coordinates: [],
        isFilled: false,
      },
    ]);
  }

  // remove location
  function handleRemoveLocationInput(index) {
    setViaLocationInputs((prevInputs) =>
      prevInputs.filter((_, i) => i !== index)
    );
  }

  // location search
  function handleViaLocationSearch(locationStr, index) {
    const clonedInputs = [...viaLocationInputs];

    // update values
    clonedInputs[index].locationStr = locationStr;
    clonedInputs[index].coordinates = isValidCoordinate(locationStr)
      ? getCoordinates(locationStr)
      : [];

    // update state
    setViaLocationInputs(clonedInputs);
  }

  // container class
  const containerClass =
    "autocomplete-map bg-white border right-0 rounded-lg p-2 my-2 mx-3";

  // show search location button
  if (!showLocationSearch) {
    return (
      <div
        className={containerClass}
        onClick={() => setShowLocationSearch(true)}
      >
        <i
          title="Search Location"
          className="fa fa-search-location fa-2x cursor-pointer"
        />
      </div>
    );
  }

  return (
    <div className={`${containerClass} min-w-300 overflow-y-auto max-h-400`}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h5 className="mb-0">Locations</h5>
        <i
          title="Close Search"
          className="fa fa-times-circle fa-2x cursor-pointer"
          onClick={() => setShowLocationSearch(false)}
        />
      </div>
      <div className="">
        {/* Start (from) */}
        <PlacesAutocomplete
          className="shadow-none rounded-lg mb-2"
          placeholder="From"
          setLocationStr={setStartLocationStr}
          locationStr={startLocationStr}
        />

        {/* Mid (via) */}
        <ViaMidPointsSection
          viaLocationInputs={viaLocationInputs}
          handleViaLocationSearch={handleViaLocationSearch}
          handleRemoveLocationInput={handleRemoveLocationInput}
        />

        {/* end (to) */}
        <PlacesAutocomplete
          className="shadow-none rounded-lg mb-2"
          placeholder="To"
          setLocationStr={setEndLocationStr}
          locationStr={endLocationStr}
        />

        {/* Footer */}
        <AddViaPointsAndGetRoutesSection
          handleAddLocationInput={handleAddLocationInput}
          handleGetRoutes={handleGetRoutes}
        />
      </div>
    </div>
  );
}

function RouteLine({ route = {} }) {
  const { path = [] } = route || {};

  // reversing order in path points
  // [long, lat] ==> [lat,lng]
  const formattedPath = path.map(([lng, lat]) => [lat, lng]);

  return (
    <Polyline pathOptions={SelectedRouteStyle} positions={formattedPath} />
  );
}

function LocationMarker({ coordinate = [], icon, locationStr, label }) {
  if (coordinate.length === 0) {
    return null;
  }

  return (
    <Marker position={coordinate} icon={icon}>
      <Popup>
        <span className="font-weight-bold">{label} : </span>
        <span>{locationStr}</span>
      </Popup>
    </Marker>
  );
}

/**
 * Page-Section
 */
function RoadStretchCreateMapView({ waypointsRoute = {} }) {
  const dispatch = useDispatch();

  // states
  const [mapZoom, setMapZoom] = useState(null);
  const [startLocationStr, setStartLocationStr] = useState("");
  const [endLocationStr, setEndLocationStr] = useState("");

  // coordinate is to show PinDrop Point on map
  const [startCoordinate, setStartCoordinate] = useState([]);
  const [endCoordinate, setEndCoordinate] = useState([]);
  const [viaLocationInputs, setViaLocationInputs] = useState([]);

  // clearing the states if no locationStr
  useEffect(() => {
    // clear waypoints route
    dispatch(clearWaypointsRoute());

    if (!startLocationStr) {
      setStartCoordinate([]);
    }

    if (!endLocationStr) {
      setEndCoordinate([]);
    }

    if (!startLocationStr || !endLocationStr) {
      dispatch(resetRoadStretches());
    }
  }, [
    dispatch,
    startLocationStr,
    endLocationStr,
    JSON.stringify(viaLocationInputs),
  ]);

  // Center
  const finalCoordinates = startCoordinate.length
    ? startCoordinate
    : endCoordinate;
  const mapCenter = finalCoordinates.length
    ? finalCoordinates
    : India.mapCenter;
  const zoom = getMapZoomLevel(finalCoordinates, mapZoom); // zoom

  // map-point-marker
  const marker = { type: "selected-poi" };
  const icon = new MapIcon({ type: marker.type });

  // get Coordinates From PinDrop
  function getCoordinatesFromPinDrop(locationData) {
    const coordinateString = `${locationData.lat},${locationData.lng}`;

    // first time update start location
    if (!startLocationStr) {
      setStartLocationStr(coordinateString);
      setStartCoordinate([locationData.lat, locationData.lng]);
      return;
    }

    // update end location
    if (!endLocationStr) {
      setEndLocationStr(coordinateString);
      setEndCoordinate([locationData.lat, locationData.lng]);
      return;
    }

    // mid points
    setViaLocationInputs((prevInputs) => {
      let isFilled = false;

      const updated = prevInputs.map((item) => {
        const { coordinates, locationStr } = item;
        if (!isFilled && coordinates.length === 0 && !locationStr) {
          isFilled = true; // only the first match gets isFilled
          return {
            ...item,
            locationStr: coordinateString,
            coordinates: [locationData.lat, locationData.lng],
          };
        }
        return item;
      });

      return updated;
    });
  }

  // start autocomplete
  function getStartCoordinatesFromAutoComplete(locationStr) {
    setStartLocationStr(locationStr);
    if (isValidCoordinate(locationStr)) {
      setStartCoordinate(getCoordinates(locationStr));
    }
  }

  // end autocomplete
  function getEndCoordinatesFromAutoComplete(locationStr) {
    setEndLocationStr(locationStr);
    if (isValidCoordinate(locationStr)) {
      setEndCoordinate(getCoordinates(locationStr));
    }
  }

  // get routes function
  function handleGetRoutes() {
    // check for start and end location
    if (!startLocationStr || !endLocationStr) {
      toast.error("Please Provide start or end point.");
      return;
    }

    // check for via location inputs locationStr
    const viaLocationStrings = viaLocationInputs.map(
      (input) => input.locationStr
    );
    const hasUnFilledLocation = viaLocationStrings.includes("");

    if (viaLocationInputs.length > 0 && hasUnFilledLocation) {
      toast.error("Please Provide via Location.");
      return;
    }

    // waypointsLocations
    const waypointsLocations = [
      startLocationStr,
      ...viaLocationStrings,
      endLocationStr,
    ];

    // api
    dispatch(getWaypointsRoute(waypointsLocations));
  }

  return (
    <div className="map-layout-right">
      <div className="org-map-cont leaflet-container">
        {/* Search by Location and LatLng Input Section */}
        <LocationSearch
          startLocationStr={startLocationStr}
          setStartLocationStr={getStartCoordinatesFromAutoComplete}
          endLocationStr={endLocationStr}
          setEndLocationStr={getEndCoordinatesFromAutoComplete}
          handleGetRoutes={handleGetRoutes}
          viaLocationInputs={viaLocationInputs}
          setViaLocationInputs={setViaLocationInputs}
        />

        {/* Map */}
        <LLMap center={mapCenter} zoom={zoom}>
          {/* PinDrop Markers */}
          {startCoordinate.length > 0 && (
            <LocationMarker
              coordinate={startCoordinate}
              icon={icon}
              locationStr={startLocationStr}
              label="From"
            />
          )}
          {endCoordinate.length > 0 && (
            <LocationMarker
              coordinate={endCoordinate}
              icon={icon}
              locationStr={endLocationStr}
              label="To"
            />
          )}

          {/* Via Points Markers */}
          {viaLocationInputs.map((input, index) => {
            const { locationStr, coordinates } = input;
            const viaLocationIcon = getViaLocationIcon(index);
            if (coordinates.length > 0) {
              return (
                <LocationMarker
                  key={index}
                  coordinate={coordinates}
                  icon={viaLocationIcon}
                  locationStr={locationStr}
                  label="Via"
                />
              );
            }
          })}

          {/* PinDropLocationAndZoom Component */}
          <PinDropLocationAndZoom
            onLocationSelect={getCoordinatesFromPinDrop}
            mapZoom={mapZoom}
            setMapZoom={setMapZoom}
          />

          {/* route line */}
          {Object.keys(waypointsRoute).length > 0 && (
            <RouteLine route={waypointsRoute} />
          )}
        </LLMap>
      </div>
    </div>
  );
}

export default RoadStretchCreateMapView;
